import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { CloseOutlined, EditOutlined } from '@ant-design/icons';
import { Row, Col, Switch, Alert, Button, Drawer } from 'antd';

import Spin from 'components/Spin';
import Truncate from 'components/Truncate';
import type { IrregularProduct } from 'scenes/Producer/Product/MyProducts/constants';

import type { QuickviewProps } from './constants';

const getActionsMessage = (product: IrregularProduct) => {
  const { pending_actions } = product;
  const messages: React.ReactNode[] = [];

  if (pending_actions.above_commission_limit) {
    messages.push(
      <div key='above_commission_limit' style={{ paddingBottom: 8 }}>
        <Alert
          type='warning'
          message='Os produtos listados apresentam comissões que ultrapassam indevidamente o limite de 100%. Para evitar que esse valor adicional seja descontado das próximas transações, normalize esse cenário até 19/02.'
        />
      </div>
    );
  }

  if (pending_actions.above_coproducers_limit) {
    messages.push(
      <div key='above_coproducers_limit' style={{ paddingBottom: 8 }}>
        <Alert
          type='warning'
          message='Os produtos listados abaixo contam com mais de 14 coprodutores cadastrados, sendo necessário a remoção dos excedentes até o dia 19/02. Por gentileza, regularize os cadastros'
        />
      </div>
    );
  }

  if (pending_actions.commission_by_value) {
    messages.push(
      <div key='commission_by_value' style={{ paddingBottom: 8 }}>
        <Alert
          type='warning'
          message='Os produtos listados abaixo estão configurados com a regra de comissão fixa por valor, a qual deixará de existir a partir do dia 19/02. Por gentileza, adapte para a regra de comissionamento percentual.'
        />
      </div>
    );
  }

  if (pending_actions.invalid_physical_dimensions) {
    messages.push(
      <div key='invalid_physical_dimensions' style={{ paddingBottom: 8 }}>
        <Alert
          type='warning'
          message='Os produtos listados abaixo estão configurados com dimensões que não são aceitas pelos correios. Por gentileza, corrija as dimensões dos produtos.'
        />
      </div>
    );
  }

  return messages;
};

const getEditButton = (product: IrregularProduct) => {
  const { id, parent_product } = product;
  const baseRoute = '/producer/product/editproduct';
  const finalRoute = parent_product
    ? `${baseRoute}/${parent_product}/${id}/coproducers`
    : `${baseRoute}/${id}/coproducers`;

  return (
    <Link to={finalRoute}>
      <Button
        type='text'
        icon={<EditOutlined style={{ fontSize: 12, color: '#596375' }} />}
        style={{
          color: '#596375',
          fontSize: 12,
          fontWeight: 600
        }}
      >
        Editar
      </Button>
    </Link>
  );
};

const QuickView: React.FC<QuickviewProps> = ({ active, changeActive, productData }) => {
  const [loading, setLoading] = useState(true);
  const [idProductHistory, setIdProductHistory] = useState<number>(null);
  const [statusProduct, setStatusProduct] = useState<'active' | 'inactive' | 'filed' | ' '>(null);

  const productDataRef = useRef(productData);
  productDataRef.current = productData;

  const prevActiveRef = useRef(active);

  const handleCloseQuickView = useCallback(() => {
    if (typeof changeActive === 'function') {
      changeActive();
    }

    setLoading(true);
    setIdProductHistory(productDataRef.current?.id);
  }, [changeActive]);

  useEffect(() => {
    const wasInactive = prevActiveRef.current === false;
    prevActiveRef.current = active;

    if (!wasInactive || !active) {
      return;
    }

    if (productData.id && productData.id !== idProductHistory) {
      setLoading(true);
      setLoading(false);
      setStatusProduct(productData.status);
    } else {
      setLoading(false);
    }
  }, [active, productData, idProductHistory]);

  const getToggleStatus = () => {
    switch (statusProduct) {
      case 'active':
        return 'Ativo';
      case 'inactive':
        return 'Inativo';
      case 'filed':
        return 'Arquivado';
      default:
        return 'Sem status';
    }
  };

  const labelToggleStatus = getToggleStatus();

  if (!productData) {
    return (
      <Drawer
        onClose={handleCloseQuickView}
        placement='right'
        open={false}
        closable={false}
        styles={{ body: { padding: 0 }, wrapper: { maxWidth: '100%' } }}
        width={450}
      />
    );
  }

  return (
    <Drawer
      onClose={handleCloseQuickView}
      placement='right'
      open={active}
      closable={false}
      styles={{ body: { padding: 0 }, wrapper: { maxWidth: '100%' } }}
      width={450}
    >
      {loading ? (
        <div className='loading' style={{ height: '100%' }}>
          <Spin tip='Carregando produto ...' />
        </div>
      ) : (
        <>
          <div className='bg-[#2d3748] text-white'>
            <div className='flex items-center justify-between'>
              <div className='px-4 text-base'>Produto: {productData.id}</div>
              <div
                className='cursor-pointer p-[0.8rem] leading-4 hover:bg-[rgba(0,0,0,0.5)] hover:opacity-60'
                onClick={handleCloseQuickView}
              >
                <CloseOutlined style={{ fontSize: 16, color: '#ccc' }} />
              </div>
            </div>
          </div>

          <div className='p-4'>
            <Row>
              <Col
                className='product__image'
                xs={8}
                sm={8}
                style={{
                  backgroundImage: `url(${productData.image ? productData.image : ''})`
                }}
              />

              <Col className='product__info' xs={16} sm={16}>
                <div className='product__name' title={productData.title ? productData.title : 'Produto desconhecido'}>
                  <Truncate count={40} final={'...'}>
                    {productData.title ? productData.title : 'Produto desconhecido'}
                  </Truncate>
                </div>

                <div className='product__status'>
                  {!productData.has_children && (
                    <div className='flex items-center gap-2'>
                      <Switch
                        defaultChecked={statusProduct === 'active'}
                        checked={statusProduct === 'active'}
                        disabled={true}
                      />
                      <span>{labelToggleStatus}</span>
                    </div>
                  )}
                </div>
              </Col>

              <Col span={24}>
                {getActionsMessage(productData)}
                {getEditButton(productData)}
              </Col>
            </Row>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default QuickView;
